import { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CommonSuspenseLoader from '../../molecules/CommonSuspenseLoader/CommonSuspenseLoader';
import { DetailViewContext } from './DetailViewContext';
import { DETAIL_VIEWS } from './detailViews.registry';

/**
 * DetailViewProvider — a single host for the app's command/query detail modals.
 *
 * Pages fire intents through `useDetailViews()`; the provider owns *which* view
 * is open, how views stack, and closing. Pages no longer keep per-modal
 * open/close state or import the widgets themselves.
 *
 * Design decisions (see the design discussion):
 * - View-agnostic: `params` from an intent are forwarded verbatim as props, so
 *   registered views keep their existing signatures (id or hydrated object).
 *   The host injects only `onClose` and `isSecondary`.
 * - No result contract: command widgets already invalidate their React Query
 *   list/detail keys on save, so listings refetch themselves. The host is
 *   fire-and-forget.
 * - Stacking by `entityKey`: same entity as the current top -> REPLACE (read ->
 *   edit the same thing); different entity -> PUSH (drill into another entity,
 *   close returns to the previous view). Lower entries stay MOUNTED so their
 *   internal state (wizard step, scroll) survives, and render with
 *   `isSecondary` so the modal layout offsets them.
 */
export const DetailViewProvider = ({ children }) => {
  // LIFO stack of open intents: { key, viewKey, entityKey, params }.
  const [stack, setStack] = useState([]);
  const seqRef = useRef(0);

  const pushIntent = useCallback(
    (
      viewKey,
      { entityKey = null, ...params } = {},
      { replace = false } = {}
    ) => {
      setStack((prev) => {
        const entry = {
          key: `dv-${(seqRef.current += 1)}`,
          viewKey,
          entityKey,
          params,
        };
        if (prev.length === 0) return [entry];
        const top = prev[prev.length - 1];
        const sameEntity = entityKey != null && top.entityKey === entityKey;
        // Same entity as the top (read -> edit), or an explicit replace: swap
        // the top out. Otherwise push a new layer over it.
        if (replace || sameEntity) {
          return [...prev.slice(0, -1), entry];
        }
        return [...prev, entry];
      });
    },
    []
  );

  const open = useCallback(
    (viewKey, intent) => pushIntent(viewKey, intent, { replace: false }),
    [pushIntent]
  );

  const replace = useCallback(
    (viewKey, intent) => pushIntent(viewKey, intent, { replace: true }),
    [pushIntent]
  );

  // Close a specific entry AND anything stacked above it (closing a parent
  // dismisses its children). Used by the injected `onClose`.
  const closeEntry = useCallback((entryKey) => {
    setStack((prev) => {
      const idx = prev.findIndex((e) => e.key === entryKey);
      return idx === -1 ? prev : prev.slice(0, idx);
    });
  }, []);

  const close = useCallback(() => setStack((prev) => prev.slice(0, -1)), []);
  const closeAll = useCallback(() => setStack([]), []);

  const api = useMemo(
    () => ({ open, replace, close, closeAll }),
    [open, replace, close, closeAll]
  );

  return (
    <DetailViewContext.Provider value={api}>
      {children}
      {stack.map((entry, index) => {
        const View = DETAIL_VIEWS[entry.viewKey];
        if (!View) {
          if (import.meta.env?.DEV) {
            console.error(
              `[DetailViews] No view registered for key "${entry.viewKey}".`
            );
          }
          return null;
        }
        return (
          <Suspense key={entry.key} fallback={<CommonSuspenseLoader />}>
            {/*
              params first, then host-owned props so the host wins: the view
              always closes through the stack, and stacking depth drives
              `isSecondary` regardless of what a caller passed.
            */}
            <View
              {...entry.params}
              isSecondary={index > 0}
              onClose={() => closeEntry(entry.key)}
            />
          </Suspense>
        );
      })}
    </DetailViewContext.Provider>
  );
};

DetailViewProvider.propTypes = {
  children: PropTypes.node,
};

DetailViewProvider.displayName = 'DetailViewProvider';

export default DetailViewProvider;
